import { useState } from "react"

// Renders a config form driven by an adapter's configSchema array.
// Props:
//   schema      — array of { key, type, label, required, help, placeholder, options }
//   value       — current config object { [key]: value }
//   onChange    — (updatedConfig) => void
//   formFields  — array of field label strings (for field_map type)
//   fieldDefs   — array of { name, label } (for sheet_columns; name is the payload key)
//   providerSlug, onCreateSheet, formTitle — for sheet_target's "Create new sheet"
export function IntegrationConfigEditor({ schema = [], value = {}, onChange, formFields = [], fieldDefs = [], providerSlug, onCreateSheet, formTitle }) {
    if (!schema.length) {
        return (
            <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 italic">
                No configuration needed for this action.
            </p>
        )
    }

    const update = (key, val) => onChange({ ...value, [key]: val })

    return (
        <div className="space-y-3">
            {schema.map(field => (
                <ConfigField
                    key={field.key}
                    field={field}
                    value={value[field.key]}
                    onChange={val => update(field.key, val)}
                    formFields={formFields}
                    fieldDefs={fieldDefs}
                    providerSlug={providerSlug}
                    onCreateSheet={onCreateSheet}
                    formTitle={formTitle}
                    allValues={value}
                />
            ))}
        </div>
    )
}

function ConfigField({ field, value, onChange, formFields, fieldDefs, providerSlug, onCreateSheet, formTitle, allValues }) {
    const { key, type, label, required, help, placeholder, options } = field
    const baseInput = "w-full text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"

    const labelEl = (
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
    )

    const helpEl = help ? (
        <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">{help}</p>
    ) : null

    if (type === "boolean") {
        return (
            <div className="flex items-center gap-2">
                <input
                    id={key}
                    type="checkbox"
                    checked={!!value}
                    onChange={e => onChange(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label htmlFor={key} className="text-sm text-gray-700 dark:text-gray-300">
                    {label}
                </label>
                {helpEl}
            </div>
        )
    }

    if (type === "select") {
        return (
            <div>
                {labelEl}
                <select
                    value={value || ""}
                    onChange={e => onChange(e.target.value)}
                    className={baseInput}
                >
                    <option value="">Select…</option>
                    {(options || []).map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
                {helpEl}
            </div>
        )
    }

    if (type === "textarea" || type === "json") {
        return (
            <div>
                {labelEl}
                <textarea
                    rows={3}
                    value={value || ""}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder}
                    className={`${baseInput} font-mono`}
                />
                {type === "json" && (
                    <JsonValidationHint value={value} />
                )}
                {helpEl}
            </div>
        )
    }

    if (type === "template") {
        return (
            <div>
                {labelEl}
                <textarea
                    rows={2}
                    value={value || ""}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder || "Use {{ fieldName }} for dynamic values"}
                    className={baseInput}
                />
                <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">
                    Use <code className="font-mono bg-gray-100 dark:bg-gray-800 px-0.5 rounded">{"{{ fieldName }}"}</code> to insert form field values.
                    {help ? ` ${help}` : ""}
                </p>
            </div>
        )
    }

    if (type === "channel_picker") {
        return (
            <div>
                {labelEl}
                <input
                    type="text"
                    value={value || ""}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder || "#channel-name or channel ID"}
                    className={baseInput}
                />
                {helpEl}
            </div>
        )
    }

    if (type === "sheet_target") {
        return (
            <div>
                {labelEl}
                <SheetTargetField
                    value={value}
                    onChange={onChange}
                    providerSlug={providerSlug}
                    onCreateSheet={onCreateSheet}
                    columns={allValues?.columns}
                    fieldDefs={fieldDefs}
                    formTitle={formTitle}
                    placeholder={placeholder || "https://docs.google.com/spreadsheets/d/…"}
                />
                {helpEl}
            </div>
        )
    }

    if (type === "sheet_columns") {
        return (
            <div>
                {labelEl}
                <SheetColumnsEditor value={value} onChange={onChange} fieldDefs={fieldDefs} />
                {helpEl}
            </div>
        )
    }

    if (type === "field_map") {
        return (
            <div>
                {labelEl}
                <FieldMapEditor
                    value={value || {}}
                    onChange={onChange}
                    formFields={formFields}
                    placeholder={placeholder || "Column (e.g. A, B, C or property name)"}
                />
                {helpEl}
            </div>
        )
    }

    // default: text / password / email / url / number
    return (
        <div>
            {labelEl}
            <input
                type={type === "password" ? "password" : "text"}
                value={value || ""}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                className={baseInput}
            />
            {helpEl}
        </div>
    )
}

// field_map editor: left = form field dropdown, right = target column/property text
function FieldMapEditor({ value, onChange, formFields, placeholder }) {
    const [newField, setNewField] = useState("")
    const [newTarget, setNewTarget] = useState("")
    const inputCls = "text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"

    const addMapping = () => {
        if (!newField || !newTarget) return
        onChange({ ...value, [newField]: newTarget })
        setNewField("")
        setNewTarget("")
    }

    const removeMapping = (fieldKey) => {
        const next = { ...value }
        delete next[fieldKey]
        onChange(next)
    }

    return (
        <div className="space-y-2">
            {Object.entries(value).map(([fk, target]) => (
                <div key={fk} className="flex items-center gap-2">
                    <span className="flex-1 text-sm px-2 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-700 dark:text-gray-300 font-medium truncate">
                        {fk}
                    </span>
                    <span className="text-gray-400">→</span>
                    <span className="flex-1 text-sm px-2 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-700 dark:text-gray-300 font-mono truncate">
                        {target}
                    </span>
                    <button
                        onClick={() => removeMapping(fk)}
                        className="text-red-400 hover:text-red-600 text-xs px-1"
                        title="Remove mapping"
                    >
                        ✕
                    </button>
                </div>
            ))}

            <div className="flex items-center gap-2">
                {formFields.length > 0 ? (
                    <select
                        value={newField}
                        onChange={e => setNewField(e.target.value)}
                        className={`flex-1 ${inputCls}`}
                    >
                        <option value="">Form field…</option>
                        {formFields.map(f => (
                            <option key={f} value={f}>{f}</option>
                        ))}
                    </select>
                ) : (
                    <input
                        type="text"
                        value={newField}
                        onChange={e => setNewField(e.target.value)}
                        placeholder="Form field name"
                        className={`flex-1 ${inputCls}`}
                    />
                )}
                <span className="text-gray-400">→</span>
                <input
                    type="text"
                    value={newTarget}
                    onChange={e => setNewTarget(e.target.value)}
                    placeholder={placeholder}
                    className={`flex-1 ${inputCls}`}
                    onKeyDown={e => e.key === "Enter" && addMapping()}
                />
                <button
                    onClick={addMapping}
                    disabled={!newField || !newTarget}
                    className="text-xs px-2 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 transition whitespace-nowrap"
                >
                    Add
                </button>
            </div>
        </div>
    )
}

// Google Sheet target: provision a new sheet in one click.
// Create-only by design — with the drive.file scope the app can only access sheets it
// creates (or ones the user picks via the Google Picker, a fast-follow). A pasted URL to a
// pre-existing sheet cannot be accessed, so there is no free-text input here.
export function SheetTargetField({ value, onChange, providerSlug, onCreateSheet, columns, fieldDefs, formTitle }) {
    const [creating, setCreating] = useState(false)
    const [err, setErr] = useState(null)
    const canCreate = providerSlug === "google_sheets" && typeof onCreateSheet === "function"
    const isUrl = typeof value === "string" && /^https?:\/\//.test(value)

    const createSheet = async () => {
        setErr(null); setCreating(true)
        try {
            const headers = (Array.isArray(columns) && columns.length)
                ? columns.map(c => c.header)
                : (fieldDefs || []).map(f => f.label)
            const out = await onCreateSheet(formTitle ? `${formTitle} — Responses` : "Form Responses", headers)
            if (out?.url) onChange(out.url)
            else if (out?.spreadsheet_id) onChange(out.spreadsheet_id)
            else setErr("Sheet created but no link was returned.")
        } catch (e) {
            setErr(e.message || "Failed to create sheet")
        } finally {
            setCreating(false)
        }
    }

    // Fallback for any non-Sheets caller of sheet_target (shouldn't happen today).
    if (!canCreate) {
        return (
            <input
                type="text"
                value={value || ""}
                onChange={e => onChange(e.target.value)}
                placeholder="Spreadsheet URL or ID"
                className="w-full text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
        )
    }

    return (
        <div className="space-y-1.5">
            {value ? (
                <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-3 py-2">
                    <span className="text-sm text-gray-700 dark:text-gray-300">✅ Responses sheet is ready.</span>
                    {isUrl && (
                        <a href={value} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">Open sheet ↗</a>
                    )}
                    <button
                        type="button"
                        onClick={createSheet}
                        disabled={creating}
                        className="ml-auto text-xs text-gray-500 dark:text-gray-400 hover:underline disabled:opacity-50"
                    >
                        {creating ? "Creating…" : "Create a different sheet"}
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={createSheet}
                    disabled={creating}
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition"
                >
                    {creating ? "Creating…" : "＋ Create a Google Sheet for responses"}
                </button>
            )}
            {err && <p className="text-xs text-red-500">{err}</p>}
        </div>
    )
}

// Google Sheet column picker: every field is a column by default; uncheck to skip,
// rename headers inline. Emits [{ field, header }] — field is the payload key.
export function SheetColumnsEditor({ value, onChange, fieldDefs = [] }) {
    const META = [
        { field: "_meta.submittedAt", label: "Submitted at" },
        { field: "_meta.formTitle", label: "Form name" },
    ]
    // undefined = untouched → default (all fields on, meta off). An array = explicit.
    const configured = Array.isArray(value)
    const cfg = (f) => (configured ? value.find(c => c.field === f) : null)

    const rows = [
        ...fieldDefs.map(f => ({
            field: f.name, label: f.label, isMeta: false,
            included: configured ? !!cfg(f.name) : true,
            header: (cfg(f.name)?.header) ?? f.label,
        })),
        ...META.map(m => ({
            field: m.field, label: m.label, isMeta: true,
            included: configured ? !!cfg(m.field) : false,
            header: (cfg(m.field)?.header) ?? m.label,
        })),
    ]

    const emit = (nextRows) =>
        onChange(nextRows.filter(r => r.included).map(r => ({ field: r.field, header: (r.header || r.label).trim() })))
    const toggle = (field) => emit(rows.map(r => r.field === field ? { ...r, included: !r.included } : r))
    const setHeader = (field, header) => emit(rows.map(r => r.field === field ? { ...r, included: true, header } : r))
    const resetAll = () => onChange(undefined)

    if (!fieldDefs.length) {
        return <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 italic">This form has no fields yet — add fields to the form and every one will be sent to your sheet automatically.</p>
    }

    const rowCls = "flex items-center gap-2"
    const includedCount = rows.filter(r => r.included).length

    return (
        <div className="space-y-2">
            <div className="flex items-start gap-2 text-[11px] text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60 rounded-lg px-2.5 py-1.5">
                <span>
                    {configured
                        ? "Checked fields become columns in your sheet. Uncheck to skip a field, or edit a header name."
                        : "✓ All fields are already selected — you don't need to do anything. Uncheck any you want to leave out, or rename a column header."}
                </span>
            </div>
            <div className="flex items-center justify-between">
                <span className="text-[11px] text-gray-400 dark:text-gray-500">{includedCount} column{includedCount === 1 ? "" : "s"} → your sheet</span>
                {configured && (
                    <button type="button" onClick={resetAll} className="text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline">
                        Reset to all fields
                    </button>
                )}
            </div>
            {rows.map(r => (
                <div key={r.field} className={rowCls}>
                    <input
                        type="checkbox"
                        checked={r.included}
                        onChange={() => toggle(r.field)}
                        className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0"
                    />
                    <span className={`flex-1 text-sm truncate ${r.included ? "text-gray-700 dark:text-gray-300" : "text-gray-400 dark:text-gray-500"} ${r.isMeta ? "italic" : ""}`}>
                        {r.label}
                    </span>
                    <span className="text-gray-400">→</span>
                    <input
                        type="text"
                        value={r.header}
                        onChange={e => setHeader(r.field, e.target.value)}
                        disabled={!r.included}
                        placeholder="Column header"
                        className="flex-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                    />
                </div>
            ))}
        </div>
    )
}

function JsonValidationHint({ value }) {
    if (!value) return null
    try {
        JSON.parse(value)
        return <p className="mt-0.5 text-xs text-green-600 dark:text-green-400">Valid JSON</p>
    } catch {
        return <p className="mt-0.5 text-xs text-red-500 dark:text-red-400">Invalid JSON</p>
    }
}
